import type { PillVariant, ResizableColumnDef } from '@protoss/ui';
import {
  Button,
  CardStackIcon,
  ColumnsIcon,
  IconSize,
  Pill,
  ResizableTable,
  SkeletonBlock,
} from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { PageErrorState } from '../components/shared/PageErrorState';
import {
  AppShell,
  MainContentShell,
  type RightRailItem,
  useModuleChatRailItem,
} from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { SalesHeaderSearch } from '../features/sales/components/SalesHeaderSearch';
import type { SalesDealSummary } from '../features/sales/services/salesApi';
import { salesApi } from '../features/sales/services/salesApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { apiFetch } from '../lib/apiClient';
import './deal-page.css';

const PAGE_SIZE = 50;

type AttributesByKey = Map<string, Map<string, string>>; // attributeKey → valueKey → label

function getDealDocStatusPresentation(status: string): { label: string; variant: PillVariant } {
  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'neutral' };
    case 'ready_for_generation':
      return { label: 'Ready for Generation', variant: 'warning' };
    case 'generated':
      return { label: 'Generated', variant: 'success' };
    default:
      return { label: status.replace(/_/g, ' '), variant: 'neutral' };
  }
}

type DealAttributeDef = {
  attributeKey: string;
  label: string;
  options: { valueKey: string; label: string }[];
};

type DealTemplateFieldDef = {
  fieldKey: string;
  label: string;
  fieldType: string;
};

const DEAL_CATEGORY_ATTR_KEY = 'deal_category';
const DEFAULT_COLUMN_KEYS = [
  'title',
  'company',
  'product',
  'dealCategory',
  'createdBy',
  'createdAt',
  'status',
];
const UI_PREF_SURFACE = 'so.deals_table';

// System columns force-injected once into pre-existing saved prefs (then user-controllable).
const FORCED_SYSTEM_COLUMNS: { key: string; after: string }[] = [
  { key: 'dealCategory', after: 'product' },
];

// One-time normalization of a loaded visibleColumns array: drop the legacy attribute-derived
// category column, then inject any forced system column not yet applied. Returns the new column
// order plus the merged set of applied forced-column keys (persisted as a marker so a deliberate
// hide sticks instead of being re-injected on the next load).
function normalizeLoadedColumns(
  cols: string[],
  applied: string[]
): { columns: string[]; applied: string[] } {
  const next = cols.filter((k) => k !== `attr:${DEAL_CATEGORY_ATTR_KEY}`);
  const mergedApplied = [...applied];
  for (const fsc of FORCED_SYSTEM_COLUMNS) {
    if (!mergedApplied.includes(fsc.key)) mergedApplied.push(fsc.key);
    if (applied.includes(fsc.key) || next.includes(fsc.key)) continue;
    const afterIdx = next.indexOf(fsc.after);
    if (afterIdx >= 0) {
      next.splice(afterIdx + 1, 0, fsc.key);
    } else {
      const createdByIdx = next.indexOf('createdBy');
      if (createdByIdx >= 0) next.splice(createdByIdx, 0, fsc.key);
      else next.push(fsc.key);
    }
  }
  return { columns: next, applied: mergedApplied };
}

// ─── Config pane content - owns local drag state ──────────────────────────
function DealsConfigPaneContent({
  visibleColumnKeys,
  onToggle,
  onReorder,
  dealAttributeDefs,
  dealTemplateFieldDefs,
}: {
  visibleColumnKeys: string[];
  onToggle: (key: string, checked: boolean) => void;
  onReorder: (newOrder: string[]) => void;
  dealAttributeDefs: DealAttributeDef[];
  dealTemplateFieldDefs: DealTemplateFieldDef[];
}) {
  const [dragOverIdx, setDragOverIdx] = React.useState<number | null>(null);
  const [addMenuOpen, setAddMenuOpen] = React.useState(false);
  const [addSearch, setAddSearch] = React.useState('');
  const dragSrcIdx = React.useRef<number | null>(null);
  const addMenuRef = React.useRef<HTMLDivElement>(null);

  const closeAddMenu = React.useCallback(() => {
    setAddMenuOpen(false);
    setAddSearch('');
  }, []);

  React.useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        closeAddMenu();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addMenuOpen, closeAddMenu]);

  // Step 1: all columns sorted alphabetically (rebuilt only when defs change)
  const allCols = React.useMemo(() => {
    const cols = [
      { key: 'title', label: 'Deal', source: 'system' as const },
      { key: 'company', label: 'Company', source: 'system' as const },
      { key: 'product', label: 'Product', source: 'system' as const },
      { key: 'dealCategory', label: 'Deal Category', source: 'system' as const },
      { key: 'createdBy', label: 'Created by', source: 'system' as const },
      { key: 'createdAt', label: 'Created', source: 'system' as const },
      { key: 'status', label: 'Status', source: 'system' as const },
      ...dealAttributeDefs
        .filter((d) => d.attributeKey !== DEAL_CATEGORY_ATTR_KEY)
        .map((d) => ({
          key: `attr:${d.attributeKey}`,
          label: d.label,
          source: 'attribute' as const,
        })),
      ...dealTemplateFieldDefs.map((f) => ({
        key: `tmpl:${f.fieldKey}`,
        label: f.label,
        source: 'template' as const,
      })),
    ];
    return cols.sort((a, b) => a.label.localeCompare(b.label));
  }, [dealAttributeDefs, dealTemplateFieldDefs]);

  // Step 2: visible cols (preserve user-defined order for the drag list)
  const visibleCols = visibleColumnKeys
    .map((key) => allCols.find((c) => c.key === key))
    .filter((c): c is (typeof allCols)[number] => c !== undefined);

  // Step 3 + 4: hidden cols minus visible, then filtered by search
  const addColumnOptions = React.useMemo(() => {
    const visSet = new Set(visibleColumnKeys);
    return allCols.filter((c) => !visSet.has(c.key));
  }, [allCols, visibleColumnKeys]);

  const filteredOptions = React.useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    if (!q) return addColumnOptions;
    return addColumnOptions.filter((c) => c.label.toLowerCase().includes(q));
  }, [addColumnOptions, addSearch]);

  return (
    <div className="sales-table__col-config">
      {visibleCols.map((col, idx) => (
        <div
          key={col.key}
          className={[
            'sales-table__col-config-item',
            'sales-table__col-config-item--draggable',
            dragOverIdx === idx ? 'sales-table__col-config-item--drag-over' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            dragSrcIdx.current = idx;
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragOverIdx(idx);
          }}
          onDragLeave={() => setDragOverIdx(null)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOverIdx(null);
            const src = dragSrcIdx.current;
            dragSrcIdx.current = null;
            if (src === null || src === idx) return;
            const newOrder = [...visibleColumnKeys];
            const moved = newOrder.splice(src, 1)[0];
            if (moved === undefined) return;
            newOrder.splice(idx, 0, moved);
            onReorder(newOrder);
          }}
        >
          <span className="sales-table__col-config-drag" aria-hidden="true">
            ⠿
          </span>
          <input type="checkbox" checked onChange={() => onToggle(col.key, false)} />
          <span className="sales-table__col-config-label">{col.label}</span>
          <span
            className={`sales-table__col-config-source sales-table__col-config-source--${col.source}`}
          >
            {col.source}
          </span>
        </div>
      ))}
      {addColumnOptions.length > 0 && (
        <>
          {visibleCols.length > 0 && <div className="sales-table__col-config-divider" />}
          <div className="sales-table__col-config-add-menu" ref={addMenuRef}>
            <button
              className="sales-table__col-config-add-btn"
              onClick={() => {
                if (addMenuOpen) {
                  closeAddMenu();
                } else {
                  setAddMenuOpen(true);
                }
              }}
              aria-expanded={addMenuOpen}
            >
              <span aria-hidden="true">+</span> Add column
            </button>
            {addMenuOpen && (
              <div className="sales-table__col-config-add-list">
                <div className="sales-table__col-config-add-search">
                  <input
                    autoFocus
                    type="text"
                    className="sales-table__col-config-add-input"
                    placeholder="Search columns…"
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') closeAddMenu();
                      if (e.key === 'Enter' && filteredOptions.length === 1) {
                        onToggle(filteredOptions[0]!.key, true);
                        closeAddMenu();
                      }
                    }}
                  />
                </div>
                <div className="sales-table__col-config-add-options">
                  {filteredOptions.length === 0 ? (
                    <div className="sales-table__col-config-add-empty">No columns found</div>
                  ) : (
                    filteredOptions.map((col) => (
                      <button
                        key={col.key}
                        className="sales-table__col-config-add-option"
                        onClick={() => {
                          onToggle(col.key, true);
                          closeAddMenu();
                        }}
                      >
                        <span className="sales-table__col-config-label">{col.label}</span>
                        <span
                          className={`sales-table__col-config-source sales-table__col-config-source--${col.source}`}
                        >
                          {col.source}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SalesDealsPageContent() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const moduleChatItem = useModuleChatRailItem();

  const [deals, setDeals] = React.useState<SalesDealSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
  const [dealAttributeDefs, setDealAttributeDefs] = React.useState<DealAttributeDef[]>([]);
  const [dealTemplateFieldDefs, setDealTemplateFieldDefs] = React.useState<DealTemplateFieldDef[]>(
    []
  );

  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState<string[]>(DEFAULT_COLUMN_KEYS);
  const [selectedUuids, setSelectedUuids] = React.useState<ReadonlySet<string>>(new Set());
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  // attributesByKey: attributeKey → (valueKey → display label)
  const attributesByKey = React.useMemo<AttributesByKey>(() => {
    const map: AttributesByKey = new Map();
    for (const def of dealAttributeDefs) {
      const optMap = new Map(def.options.map((o) => [o.valueKey, o.label]));
      map.set(def.attributeKey, optMap);
    }
    return map;
  }, [dealAttributeDefs]);

  // Fetch deal attribute definitions (for real labels + optional column list).
  React.useEffect(() => {
    if (!workspace) return;
    void apiFetch<{ data: DealAttributeDef[] }>('/api/sales/deals/attribute-defs').then(
      ({ data }) => {
        if (Array.isArray(data)) setDealAttributeDefs(data);
      }
    );
    void apiFetch<{ data: DealTemplateFieldDef[] }>('/api/sales/deals/template-field-defs').then(
      ({ data }) => {
        if (Array.isArray(data)) setDealTemplateFieldDefs(data);
      }
    );
  }, [workspace?.uuid]);

  // Persist effect only fires after this ref is armed (prefs loaded), preventing
  // DEFAULT_COLUMN_KEYS from overwriting saved prefs in StrictMode double-invoke.
  const prefsLoadedRef = React.useRef(false);
  // Forced system columns already applied to this user's prefs (persisted as a marker so a
  // deliberate hide sticks instead of being re-injected on the next load).
  const appliedSystemColsRef = React.useRef<string[]>([]);

  // Load column preferences from the API once the workspace is known.
  React.useEffect(() => {
    if (!workspace) return;
    void apiFetch<{ data: { visibleColumns?: string[]; appliedSystemColumns?: string[] } }>(
      `/api/workspace/ui-preference/${UI_PREF_SURFACE}`
    ).then(({ data }) => {
      const cols = data?.visibleColumns;
      const applied = Array.isArray(data?.appliedSystemColumns) ? data.appliedSystemColumns : [];
      if (Array.isArray(cols) && cols.length > 0 && cols.every((k) => typeof k === 'string')) {
        const normalized = normalizeLoadedColumns(cols as string[], applied);
        appliedSystemColsRef.current = normalized.applied;
        setVisibleColumnKeys(normalized.columns);
      } else {
        // No saved prefs: defaults already include the forced system columns.
        appliedSystemColsRef.current = FORCED_SYSTEM_COLUMNS.map((f) => f.key);
      }
      // Arm the persist guard now - after this point, changes to visibleColumnKeys
      // reflect intentional user edits or the just-loaded prefs, never the default.
      prefsLoadedRef.current = true;
    });
  }, [workspace?.uuid]);

  const loadDeals = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedUuids(new Set());
    try {
      const dealsResult = await salesApi.listDeals();
      setDeals(dealsResult.deals);
    } catch {
      setError('Failed to load deals.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadDeals();
  }, [loadDeals]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleSort = React.useCallback(
    (key: string) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDir('asc');
      } else if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortKey(null);
        setSortDir('asc');
      }
    },
    [sortKey, sortDir]
  );

  const toggleSelectDeal = React.useCallback(
    (uuid: string, e: React.MouseEvent | React.ChangeEvent) => {
      e.stopPropagation();
      setSelectedUuids((prev) => {
        const next = new Set(prev);
        if (next.has(uuid)) next.delete(uuid);
        else next.add(uuid);
        return next;
      });
    },
    []
  );

  const handleBulkDelete = React.useCallback(async () => {
    if (isDeleting || selectedUuids.size === 0) return;
    setIsDeleting(true);
    try {
      await salesApi.softDeleteDeals([...selectedUuids]);
      setSelectedUuids(new Set());
      await loadDeals();
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  }, [isDeleting, selectedUuids, loadDeals]);

  const openConfirmDelete = React.useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const toggleColumn = React.useCallback((key: string, checked: boolean) => {
    setVisibleColumnKeys((prev) =>
      checked ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((k) => k !== key)
    );
  }, []);

  const reorderColumns = React.useCallback((newOrder: string[]) => {
    setVisibleColumnKeys(newOrder);
  }, []);

  // Persist column preferences to the API whenever the user changes them.
  // Only fires after prefsLoadedRef is set to prevent overwriting saved prefs with defaults.
  React.useEffect(() => {
    if (!prefsLoadedRef.current) return;
    if (!workspace) return;
    void apiFetch(`/api/workspace/ui-preference/${UI_PREF_SURFACE}`, {
      method: 'PUT',
      body: JSON.stringify({
        visibleColumns: visibleColumnKeys,
        appliedSystemColumns: appliedSystemColsRef.current,
      }),
    });
  }, [visibleColumnKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredDeals = React.useMemo(() => {
    if (!normalizedQuery) return deals;

    // Build haystack purely from visibleColumnKeys - only what the user has configured is searched.
    return deals.filter((deal) => {
      const parts: (string | null | undefined)[] = [];
      for (const key of visibleColumnKeys) {
        switch (key) {
          case 'title':
            // title column renders as company.name when no title, so include both
            parts.push(deal.title, deal.company.name);
            break;
          case 'company':
            parts.push(deal.company.name);
            break;
          case 'product':
            parts.push(deal.vendorProduct?.productName);
            break;
          case 'dealCategory': {
            const catKey = deal.dealCategoryKey;
            parts.push(catKey);
            // Also include resolved label so "Greenfield" matches stored key "greenfield"
            if (catKey) parts.push(attributesByKey.get(DEAL_CATEGORY_ATTR_KEY)?.get(catKey));
            break;
          }
          case 'createdBy':
            parts.push(deal.createdByDisplayName);
            break;
          case 'status':
            parts.push(deal.status);
            break;
          // createdAt intentionally omitted - ISO string won't match human-readable input
          default:
            if (key.startsWith('attr:')) {
              const attrKey = key.slice(5);
              const rawVal = deal.fieldValues?.[attrKey] ?? null;
              parts.push(rawVal);
              // Also include resolved label so "Draft" matches stored key "draft"
              if (rawVal) parts.push(attributesByKey.get(attrKey)?.get(rawVal));
            } else if (key.startsWith('tmpl:')) {
              parts.push(deal.fieldValues?.[key.slice(5)]);
            }
        }
      }
      const haystack = parts.filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [deals, normalizedQuery, visibleColumnKeys, attributesByKey]);

  const sortedDeals = React.useMemo(() => {
    if (!sortKey) return filteredDeals;
    return [...filteredDeals].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortKey === 'title') {
        aVal = a.title || a.company.name;
        bVal = b.title || b.company.name;
      } else if (sortKey === 'company') {
        aVal = a.company.name;
        bVal = b.company.name;
      } else if (sortKey === 'status') {
        aVal = a.status;
        bVal = b.status;
      } else if (sortKey === 'product') {
        aVal = a.vendorProduct?.productName ?? '';
        bVal = b.vendorProduct?.productName ?? '';
      } else if (sortKey === 'dealCategory') {
        aVal = a.dealCategoryKey ?? '';
        bVal = b.dealCategoryKey ?? '';
      } else if (sortKey === 'createdBy') {
        aVal = a.createdByDisplayName ?? '';
        bVal = b.createdByDisplayName ?? '';
      } else if (sortKey === 'createdAt') {
        aVal = a.createdAt ?? '';
        bVal = b.createdAt ?? '';
      } else if (sortKey.startsWith('attr:')) {
        const attrKey = sortKey.slice(5);
        aVal = a.fieldValues?.[attrKey] ?? '';
        bVal = b.fieldValues?.[attrKey] ?? '';
      } else if (sortKey.startsWith('tmpl:')) {
        const fieldKey = sortKey.slice(5);
        const fieldDef = dealTemplateFieldDefs.find((f) => f.fieldKey === fieldKey);
        aVal = a.fieldValues?.[fieldKey] ?? '';
        bVal = b.fieldValues?.[fieldKey] ?? '';
        if (fieldDef?.fieldType === 'number') {
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          const numCmp = (isNaN(aNum) ? -Infinity : aNum) - (isNaN(bNum) ? -Infinity : bNum);
          return sortDir === 'asc' ? numCmp : -numCmp;
        }
      }
      const cmp = aVal.localeCompare(bVal);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredDeals, sortKey, sortDir, dealTemplateFieldDefs]);

  const paginatedDeals = React.useMemo(
    () => sortedDeals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedDeals, page]
  );

  const selectedCount = selectedUuids.size;
  const allSelected =
    paginatedDeals.length > 0 && paginatedDeals.every((d) => selectedUuids.has(d.uuid));
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleSelectAll = React.useCallback(() => {
    setSelectedUuids(allSelected ? new Set() : new Set(paginatedDeals.map((d) => d.uuid)));
  }, [allSelected, paginatedDeals]);

  const controlColumn = React.useMemo<ResizableColumnDef<SalesDealSummary>>(
    () => ({
      key: '__select',
      label: (
        <input
          type="checkbox"
          className="rt-table__checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={toggleSelectAll}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select all deals"
        />
      ),
      defaultWidth: 40,
      minWidth: 40,
      maxWidth: 40,
      headerClassName: 'rt-table__col--checkbox',
      cellClassName: 'rt-table__col--checkbox',
      stopRowClick: true,
      skeletonContent: (
        <div className="rt-table__skeleton-cell rt-table__skeleton-cell--square">
          <SkeletonBlock />
        </div>
      ),
      render: (deal) => (
        <input
          type="checkbox"
          className="rt-table__checkbox"
          checked={selectedUuids.has(deal.uuid)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => toggleSelectDeal(deal.uuid, e)}
          aria-label={`Select deal ${deal.title}`}
        />
      ),
    }),
    [allSelected, someSelected, selectedUuids, toggleSelectAll, toggleSelectDeal]
  );

  const columns = React.useMemo<ResizableColumnDef<SalesDealSummary>[]>(() => {
    const allCols: ResizableColumnDef<SalesDealSummary>[] = [
      {
        key: 'title',
        label: 'Deal',
        defaultWidth: 260,
        minWidth: 120,
        sortable: true,
        render: (deal) => (
          <span className="sales-table__primary">{deal.title || deal.company.name}</span>
        ),
      },
      {
        key: 'company',
        label: 'Company',
        defaultWidth: 200,
        minWidth: 100,
        sortable: true,
        render: (deal) => <span className="sales-table__secondary">{deal.company.name}</span>,
      },
      {
        key: 'product',
        label: 'Product',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (deal) => (
          <span className="sales-table__secondary">{deal.vendorProduct?.productName ?? '-'}</span>
        ),
      },
      {
        key: 'dealCategory',
        label: 'Deal Category',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (deal) => {
          const key = deal.dealCategoryKey;
          if (!key) return <span className="sales-table__secondary">-</span>;
          const label = attributesByKey.get(DEAL_CATEGORY_ATTR_KEY)?.get(key) ?? key;
          return <span className="sales-table__secondary">{label}</span>;
        },
      },
      {
        key: 'createdBy',
        label: 'Created by',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (deal) => (
          <span className="sales-table__secondary">{deal.createdByDisplayName ?? '-'}</span>
        ),
      },
      {
        key: 'createdAt',
        label: 'Created',
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (deal) => (
          <span className="sales-table__date">
            {deal.createdAt
              ? new Date(deal.createdAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '-'}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        defaultWidth: 185,
        minWidth: 100,
        sortable: true,
        wrap: true,
        render: (deal) => {
          const { label, variant } = getDealDocStatusPresentation(deal.status);
          return <Pill variant={variant}>{label}</Pill>;
        },
      },
      ...dealAttributeDefs
        .filter((d) => d.attributeKey !== DEAL_CATEGORY_ATTR_KEY)
        .map((d) => ({
          key: `attr:${d.attributeKey}`,
          label: d.label,
          defaultWidth: 140,
          minWidth: 80,
          sortable: true,
          render: (deal: SalesDealSummary) => {
            const val = deal.fieldValues?.[d.attributeKey] ?? null;
            if (!val) return <span className="sales-table__secondary">-</span>;
            const optLabel = attributesByKey.get(d.attributeKey)?.get(val) ?? val;
            return <span className="sales-table__secondary">{optLabel}</span>;
          },
        })),
      ...dealTemplateFieldDefs.map((f) => ({
        key: `tmpl:${f.fieldKey}`,
        label: f.label,
        defaultWidth: 160,
        minWidth: 80,
        sortable: true,
        render: (deal: SalesDealSummary) => (
          <span className="sales-table__secondary">{deal.fieldValues?.[f.fieldKey] ?? '-'}</span>
        ),
      })),
    ];
    const userCols = visibleColumnKeys
      .map((key) => {
        const col = allCols.find((c) => c.key === key);
        if (col) return col;
        // Placeholder for attr: columns that haven't loaded from dealAttributeDefs yet
        // (race: prefs resolve before attribute-defs resolve).
        if (key.startsWith('attr:')) {
          const attrKey = key.slice(5);
          return {
            key,
            label: attrKey,
            defaultWidth: 140,
            minWidth: 80,
            sortable: false,
            render: () => <span className="sales-table__secondary">-</span>,
          } as ResizableColumnDef<SalesDealSummary>;
        }
        return undefined;
      })
      .filter((col): col is ResizableColumnDef<SalesDealSummary> => col !== undefined);
    return [controlColumn, ...userCols];
  }, [attributesByKey, visibleColumnKeys, dealAttributeDefs, dealTemplateFieldDefs, controlColumn]);

  if (isLoadingWorkspace) {
    return null;
  }

  const leftNav = (
    <AppNav
      selected="deals"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const headerActions = (
    <>
      <SalesHeaderSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search deals…"
        ariaLabel="Search deals"
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void loadDeals()}
        disabled={loading}
      >
        Refresh
      </Button>
    </>
  );

  // Module chat first so the AI icon stays on top; Configure sits below it.
  // Providing the isModuleChat item ourselves stops AppShell appending a duplicate.
  const rightRailItems: RightRailItem[] = [
    ...(moduleChatItem ? [moduleChatItem] : []),
    {
      id: 'deals-columns',
      label: 'Configure columns',
      icon: <ColumnsIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
      content: (
        <DealsConfigPaneContent
          visibleColumnKeys={visibleColumnKeys}
          onToggle={toggleColumn}
          onReorder={reorderColumns}
          dealAttributeDefs={dealAttributeDefs}
          dealTemplateFieldDefs={dealTemplateFieldDefs}
        />
      ),
    },
  ];

  const main = (
    <MainContentShell title="Deals" actions={headerActions}>
      {error ? (
        <PageErrorState message={error} onRetry={() => void loadDeals()} />
      ) : (
        <div className="sales-table__container">
          {selectedCount > 0 && (
            <div
              className="sales-table__bulk-toolbar"
              role="toolbar"
              aria-label="Bulk deal actions"
            >
              <span className="sales-table__bulk-count">
                {selectedCount} deal{selectedCount !== 1 ? 's' : ''} selected
              </span>
              <button
                type="button"
                className="sales-table__bulk-delete-btn"
                onClick={() => openConfirmDelete()}
                disabled={isDeleting}
              >
                {isDeleting
                  ? 'Deleting…'
                  : `Delete ${selectedCount} deal${selectedCount !== 1 ? 's' : ''}`}
              </button>
              <button
                type="button"
                className="sales-table__bulk-clear-btn"
                onClick={() => setSelectedUuids(new Set())}
              >
                Clear
              </button>
            </div>
          )}
          <ResizableTable
            columns={columns}
            rows={paginatedDeals}
            getRowKey={(deal) => deal.uuid}
            aria-label="Deals"
            hasSelectionColumn
            sortKey={sortKey ?? undefined}
            sortDir={sortDir}
            onSort={handleSort}
            onRowClick={(deal) => navigate(`/so/deal/${deal.uuid}`)}
            getRowClassName={(deal) =>
              selectedUuids.has(deal.uuid) ? 'sales-tr--selected' : undefined
            }
            loading={loading}
            emptyState={
              <div className="sales-table__empty">
                <CardStackIcon
                  width={IconSize.xl}
                  height={IconSize.xl}
                  aria-hidden="true"
                  className="sales-table__empty-icon"
                />
                <span>{normalizedQuery ? 'No deals match your search.' : 'No deals yet.'}</span>
              </div>
            }
            pagination={
              sortedDeals.length > PAGE_SIZE
                ? {
                    page,
                    pageSize: PAGE_SIZE,
                    total: sortedDeals.length,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </div>
      )}
    </MainContentShell>
  );

  const singleDeal = selectedUuids.size === 1 ? deals.find((d) => selectedUuids.has(d.uuid)) : null;

  return (
    <>
      <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={selectedUuids.size === 1 ? 'Delete deal?' : `Delete ${selectedUuids.size} deals?`}
        confirmKeyword={singleDeal ? singleDeal.title : 'yes'}
        matchMode={singleDeal ? 'exact' : 'caseInsensitive'}
        description={
          singleDeal
            ? 'This action cannot be undone.'
            : `This will permanently delete ${selectedUuids.size} deals and cannot be undone.`
        }
        isPending={isDeleting}
        onConfirm={handleBulkDelete}
      />
    </>
  );
}

export function SalesDealsPage() {
  return <SalesDealsPageContent />;
}
